import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Download, Loader2, Play, XCircle } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NoticeBanner } from "@/components/ui/notice-banner";
import { PageNote } from "@/components/ui/page-legend";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/auth/use-auth";
import { outboundApi } from "@/features/outbound/outbound-api";
import { polfarmaMshrApi, type RecomputeReport } from "@/features/polfarma-mshr/polfarma-mshr-api";
import { ensureElementVisible } from "@/lib/route-guards";

export const Route = createFileRoute("/_auth/konfiguracja/import-mshr")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(context.queryClient, "sidebar.system.import-mshr", location.href);
	},
	component: ImportMshrPage,
});

function todayMinusDays(days: number): string {
	const d = new Date();
	d.setDate(d.getDate() - days);
	return d.toISOString().slice(0, 10);
}

function todayIso(): string {
	return new Date().toISOString().slice(0, 10);
}

function ImportMshrPage() {
	const { user } = useAuth();
	const isSuperAdmin = !!user?.roles?.includes("super_admin");
	const qc = useQueryClient();
	const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);
	const [dateFrom, setDateFrom] = useState(todayMinusDays(14));
	const [dateTo, setDateTo] = useState(todayIso());
	const [error, setError] = useState<string | null>(null);
	const [recomputeReport, setRecomputeReport] = useState<RecomputeReport | null>(null);

	const connectionsQuery = useQuery({
		queryKey: ["outbound", "connections", "mshr"],
		queryFn: () => outboundApi.listConnections(false),
		enabled: isSuperAdmin,
	});

	const statusQuery = useQuery({
		queryKey: ["polfarma-mshr", "status", selectedConnectionId],
		queryFn: () => polfarmaMshrApi.getStatus(selectedConnectionId as number),
		enabled: selectedConnectionId != null && isSuperAdmin,
		refetchInterval: 30_000,
	});

	const pullZestawMutation = useMutation({
		mutationFn: () => polfarmaMshrApi.pullZestaw(selectedConnectionId as number),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["polfarma-mshr", "status"] });
			setError(null);
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad pull mshr_zestaw"),
	});

	const pullProduktyMutation = useMutation({
		mutationFn: () => polfarmaMshrApi.pullProdukty(selectedConnectionId as number),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["polfarma-mshr", "status"] });
			setError(null);
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad pull mshr_produkty"),
	});

	const recomputeMutation = useMutation({
		mutationFn: () =>
			polfarmaMshrApi.recompute(selectedConnectionId as number, {
				dateFrom,
				dateTo,
			}),
		onSuccess: (report) => {
			setRecomputeReport(report);
			qc.invalidateQueries({ queryKey: ["polfarma-mshr", "status"] });
			setError(null);
		},
		onError: (e: unknown) => {
			setRecomputeReport(null);
			setError(e instanceof Error ? e.message : "Blad recompute");
		},
	});

	const setZestawMutation = useMutation({
		mutationFn: ({ idzestawu, aktywny }: { idzestawu: number; aktywny: boolean }) =>
			polfarmaMshrApi.setZestawAktywny(idzestawu, aktywny),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["polfarma-mshr", "status"] });
			setError(null);
		},
		onError: (e: unknown) => setError(e instanceof Error ? e.message : "Blad toggle zestaw"),
	});

	if (!isSuperAdmin) {
		return (
			<div className="p-6">
				<p className="text-muted-foreground">Brak dostepu. Wymagana rola super_admin.</p>
			</div>
		);
	}

	const selectedConnection = connectionsQuery.data?.find((c) => c.id === selectedConnectionId);
	const status = statusQuery.data;

	return (
		<div className="p-6 space-y-4 max-w-[1400px] max-md:p-3 max-md:space-y-3">
			<div>
				<h1 className="text-2xl font-semibold max-md:hidden">Import MSHR (Polfarma)</h1>
				<PageNote id="about" title="O stronie">
					<p className="text-sm text-muted-foreground max-md:break-words">
						Pobieranie tabel mshr_zestaw + mshr_produkty z SQL Server Polfarmy i przeliczanie
						udzialow PPH per apteka. Tabele i funkcje siedzą w prima.polfarma_*. Apteki iterujemy z
						pinowanych kontraktow oznaczonych w SFY flaga "MSHR".
					</p>
				</PageNote>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Polaczenie</CardTitle>
					<CardDescription>
						Wybierz SFY connection Polfarmy (musi byc skonfigurowane w /konfiguracja/sfy).
					</CardDescription>
				</CardHeader>
				<CardContent>
					{connectionsQuery.isLoading ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : connectionsQuery.data && connectionsQuery.data.length > 0 ? (
						<Select
							value={selectedConnectionId ? String(selectedConnectionId) : ""}
							onValueChange={(v) => setSelectedConnectionId(Number(v))}
						>
							<SelectTrigger className="max-w-2xl max-md:w-full max-md:[&>span]:min-w-0">
								<SelectValue placeholder="Wybierz polaczenie Polfarmy" />
							</SelectTrigger>
							<SelectContent>
								{connectionsQuery.data.map((c) => (
									<SelectItem key={c.id} value={String(c.id)}>
										{c.clientName} — {c.name} ({c.host}:{c.port}/{c.databaseName})
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : (
						<p className="text-sm text-muted-foreground">
							Brak polaczen. Utworz najpierw w /konfiguracja/sfy.
						</p>
					)}
				</CardContent>
			</Card>

			{error && (
				<NoticeBanner
					tone="error"
					title="Blad"
					description={error}
					onDismiss={() => setError(null)}
				/>
			)}

			{selectedConnection && (
				<>
					<Card>
						<CardHeader>
							<CardTitle>Pobieranie tabel z Polfarmy</CardTitle>
							<CardDescription>
								Truncate + pelne nadpisanie. mshr_zestaw zawiera definicje zestawow (kolumna
								`aktywny` decyduje co liczymy), mshr_produkty — produkty per lista (Total/PPH).
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-2">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								<div className="border rounded-md p-3 space-y-2">
									<div className="flex items-center justify-between">
										<div>
											<div className="font-medium">mshr_zestaw</div>
											<div className="text-xs text-muted-foreground">
												{status ? `${status.zestawRowCount} wierszy` : "..."}
											</div>
										</div>
										<Button
											size="sm"
											onClick={() => pullZestawMutation.mutate()}
											disabled={pullZestawMutation.isPending}
											className="max-md:h-9"
										>
											{pullZestawMutation.isPending ? (
												<Loader2 className="h-3 w-3 animate-spin" />
											) : (
												<>
													<Download className="mr-1 h-3 w-3" /> Pobierz
												</>
											)}
										</Button>
									</div>
									{pullZestawMutation.data && (
										<div className="text-xs text-muted-foreground">
											Pobrano {pullZestawMutation.data.rowsInserted} wierszy w{" "}
											{pullZestawMutation.data.durationMs} ms
										</div>
									)}
								</div>

								<div className="border rounded-md p-3 space-y-2">
									<div className="flex items-center justify-between">
										<div>
											<div className="font-medium">mshr_produkty</div>
											<div className="text-xs text-muted-foreground">
												{status ? `${status.produktyRowCount} wierszy` : "..."}
											</div>
										</div>
										<Button
											size="sm"
											onClick={() => pullProduktyMutation.mutate()}
											disabled={pullProduktyMutation.isPending}
											className="max-md:h-9"
										>
											{pullProduktyMutation.isPending ? (
												<Loader2 className="h-3 w-3 animate-spin" />
											) : (
												<>
													<Download className="mr-1 h-3 w-3" /> Pobierz
												</>
											)}
										</Button>
									</div>
									{pullProduktyMutation.data && (
										<div className="text-xs text-muted-foreground">
											Pobrano {pullProduktyMutation.data.rowsInserted} wierszy w{" "}
											{pullProduktyMutation.data.durationMs} ms
										</div>
									)}
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Zestawy</CardTitle>
							<CardDescription>
								Zaznacz "aktywny" zeby wlaczyc zestaw do przeliczenia. Polfarma NIE ustawia tego w
								swojej tabeli — informuje recznie ktory zestaw policzyc, wiec flaga jest zarzadzana
								tutaj (zachowywana miedzy pobraniami). "Przelicz wszystko" leci po aktywnych.
							</CardDescription>
						</CardHeader>
						<CardContent>
							{status && status.zestawy.length > 0 ? (
								<div className="border rounded-md overflow-hidden max-md:overflow-x-auto">
									<table className="w-full text-sm">
										<thead className="bg-muted/50">
											<tr>
												<th className="text-left p-2 font-medium w-20">aktywny</th>
												<th className="text-left p-2 font-medium">idzestawu</th>
												<th className="text-left p-2 font-medium">zestaw</th>
												<th className="text-left p-2 font-medium">opis</th>
											</tr>
										</thead>
										<tbody>
											{status.zestawy.map((z) => (
												<tr key={z.idzestawu} className="border-t">
													<td className="p-2">
														<input
															type="checkbox"
															checked={z.aktywny}
															disabled={setZestawMutation.isPending}
															onChange={(e) =>
																setZestawMutation.mutate({
																	idzestawu: z.idzestawu,
																	aktywny: e.target.checked,
																})
															}
															className="rounded border-input max-md:size-5"
														/>
													</td>
													<td className="p-2 font-mono">{z.idzestawu}</td>
													<td className="p-2">{z.zestaw}</td>
													<td className="p-2 text-muted-foreground">{z.opis}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : (
								<p className="text-sm text-muted-foreground">
									Brak zestawow. Pobierz mshr_zestaw powyzej.
								</p>
							)}
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Przeliczenie</CardTitle>
							<CardDescription>
								5 krokow per zestaw: 4 outer-funkcje (caly/caly_nref/pph_caly/pph_caly_nref) + final
								aggregation mshr_wyniki_zestaw. Iteruje apteki z kontraktow oznaczonych flaga "MSHR"
								w SFY.
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="flex items-end gap-3 flex-wrap">
								<div className="space-y-1 max-md:min-w-[8.5rem] max-md:flex-1">
									<Label>Od</Label>
									<Input
										type="date"
										value={dateFrom}
										onChange={(e) => setDateFrom(e.target.value)}
										max={dateTo}
										min={todayMinusDays(365 * 2)}
									/>
								</div>
								<div className="space-y-1 max-md:min-w-[8.5rem] max-md:flex-1">
									<Label>Do</Label>
									<Input
										type="date"
										value={dateTo}
										onChange={(e) => setDateTo(e.target.value)}
										min={dateFrom}
										max={todayIso()}
									/>
								</div>
								<Button
									onClick={() => recomputeMutation.mutate()}
									disabled={recomputeMutation.isPending || !dateFrom || !dateTo}
									className="max-md:h-11 max-md:w-full"
								>
									{recomputeMutation.isPending ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<>
											<Play className="mr-2 h-4 w-4" /> Przelicz wszystko
										</>
									)}
								</Button>
							</div>

							{status && (
								<div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
									<MshrCount
										label="obrot_apteki_caly"
										value={status.outputRowCounts.obrotAptekiCaly}
									/>
									<MshrCount
										label="obrot_apteki_caly_nref"
										value={status.outputRowCounts.obrotAptekiCalyNref}
									/>
									<MshrCount
										label="obrot_apteki_pph_caly"
										value={status.outputRowCounts.obrotAptekiPphCaly}
									/>
									<MshrCount
										label="obrot_apteki_pph_caly_nref"
										value={status.outputRowCounts.obrotAptekiPphCalyNref}
									/>
									<MshrCount label="mshr_wyniki" value={status.outputRowCounts.mshrWyniki} />
								</div>
							)}

							{recomputeReport && <RecomputeReportPanel report={recomputeReport} />}
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
}

function MshrCount({ label, value }: { label: string; value: number }) {
	return (
		<div className="border rounded-md p-2">
			<div className="font-mono text-xs text-muted-foreground truncate" title={label}>
				{label}
			</div>
			<div className="font-medium text-sm">{value.toLocaleString("pl-PL")}</div>
		</div>
	);
}

function RecomputeReportPanel({ report }: { report: RecomputeReport }) {
	const allOk = report.steps.every((s) => s.ok);
	return (
		<div className="border rounded-md p-3 space-y-2 bg-muted/20">
			<div className="flex items-center gap-2 max-md:flex-wrap">
				{allOk ? (
					<CheckCircle2 className="h-5 w-5 text-[var(--success)]" />
				) : (
					<XCircle className="h-5 w-5 text-destructive" />
				)}
				<strong>Recompute report</strong>
				<Badge variant={allOk ? "default" : "secondary"}>
					{report.steps.length} krokow · {report.totalDurationMs} ms
				</Badge>
			</div>
			<div className="text-xs space-y-1 max-h-64 overflow-y-auto">
				{report.steps.map((s) => (
					<div
						key={`${s.step}-${s.idzestawu}`}
						className={`flex items-center gap-2 max-md:flex-wrap ${s.ok ? "" : "text-destructive"}`}
					>
						{s.ok ? (
							<CheckCircle2 className="h-3 w-3 text-[var(--success)] shrink-0" />
						) : (
							<XCircle className="h-3 w-3 shrink-0" />
						)}
						<span className="font-mono">{s.step}</span>
						<span className="text-muted-foreground">id={s.idzestawu}</span>
						<span className="text-muted-foreground">{s.durationMs} ms</span>
						{s.error && <span className="text-destructive break-all">{s.error}</span>}
					</div>
				))}
			</div>
		</div>
	);
}
